// Betalingskilde for bilag: hvilken konto kjøpet faktisk ble betalt fra.
//
// Selve bilag–bank-matchingen eies av backend. Klienten leser bare det
// persisterte beviset og supplerer med registrerte kort og konservative forslag.
//
// Den negative: «betalingen finnes ikke i banken» er i seg selv ingen
// slutning — den vanligste årsaken er at brukeren ikke har importert
// bankutskriften ennå. Først når foretakets egen bank *dekker perioden*
// bilaget ligger i, og betalingen likevel ikke finnes der, er privat utlegg en
// velbegrunnet slutning.
//
// Resultatet er alltid et forslag. Bokføring krever fortsatt at brukeren
// bekrefter kilden.
(function initReceiptPaymentSource(global) {
  const list = (value) => (Array.isArray(value) ? value : []);
  const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));

  const normalizeText = (value) => String(value || '')
    .toLocaleLowerCase('nb-NO')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  // Et kortkjøp belastes kontoen én til tre dager etter kjøpet. Skal «ikke
  // funnet i banken» bety noe, må utskriften strekke seg *forbi* bilagsdatoen —
  // ellers konkluderes privat på et kjøp som belastes dagen etter at utskriften
  // slutter. Perioden utledes dessuten av første og siste linjedato i fila, så
  // den er allerede et minimum av hva fila egentlig dekker.
  const SETTLEMENT_DAYS = 5;

  const addDays = (date, days) => new Date(new Date(`${date}T00:00:00Z`).getTime() + days * 86400000)
    .toISOString()
    .slice(0, 10);

  // Kontoer det kan handles fra. Sparekonto og private kontoer holdes utenfor:
  // et kortkjøp havner ikke der, og et manglende sparekontoutdrag skal ikke
  // blokkere slutningen.
  const companyPaymentAccounts = (state) => list(state && state.settings && state.settings.bankAccounts)
    .filter((account) => account && account.active !== false)
    .filter((account) => {
      const kind = String(account.kind || '').toLowerCase();
      return kind !== 'private' && kind !== 'savings';
    });

  const hasConfiguredBusinessBankAccount = (state) => companyPaymentAccounts(state).some((account) => {
    const accountCode = String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || '').trim();
    const accountNumber = String(account?.accountNumber || account?.number || account?.iban || '').replace(/\s/g, '');
    return Boolean(accountCode && accountNumber);
  });

  // Bankdekning utledes av periodene på importerte bankutskrifter. Bare
  // foretakets egne utskrifter teller for den negative slutningen — en privat
  // utskrift sier ingenting om hva som er betalt fra bedriftskontoen.
  //
  // Har foretaket flere betalingskontoer, må alle være dekket. En betaling vi
  // ikke finner i den ene utskriften kan ligge i den andre, og da er «ikke
  // funnet» ingen slutning i det hele tatt.
  const coverageForDate = (state, date, options) => {
    const settlementDays = Number((options || {}).settlementDays);
    const window = Number.isFinite(settlementDays) && settlementDays >= 0 ? settlementDays : SETTLEMENT_DAYS;
    const dateKey = String(date || '').slice(0, 10);
    if (!isIsoDate(dateKey)) return { covered: false, statements: [], uncoveredAccounts: [] };
    const settledBy = addDays(dateKey, window);
    const statements = list(state && state.bankStatements).filter((statement) => {
      if (statement.source && statement.source !== 'business') return false;
      const from = String(statement.periodFrom || '').slice(0, 10);
      const to = String(statement.periodTo || '').slice(0, 10);
      return isIsoDate(from) && isIsoDate(to) && from <= dateKey && settledBy <= to;
    });
    const coveredAccountIds = new Set(statements.map((statement) => String(statement.bankAccountId || '')));
    const uncoveredAccounts = companyPaymentAccounts(state)
      .filter((account) => !coveredAccountIds.has(String(account.id || '')))
      .map((account) => ({ id: account.id, name: account.name || '' }));
    return {
      covered: statements.length > 0 && uncoveredAccounts.length === 0,
      statements,
      uncoveredAccounts,
    };
  };

  const privateSettlementForState = (state) => {
    const settings = (state && state.settings) || {};
    const configured = String(settings.privateOutlayAccountCode || settings.ownerOutlayAccountCode || settings.shareholderOutlayAccountCode || '').replace(/\D/g, '').slice(0, 4);
    if (configured) return { accountCode: configured, requiresReview: false };
    const scope = global.PlummeAccountingScope?.resolveAccountingScope?.(settings, state?.activeYear);
    if (scope?.jurisdiction === 'NO' && scope?.entityFamily === 'sole_trader') return { accountCode: '2060', requiresReview: false };
    if (scope?.jurisdiction === 'NO' && scope?.entityFamily === 'limited_company') return { accountCode: '2915', requiresReview: false };
    const country = String(settings.country || 'NO').toUpperCase();
    const companyTypeCode = String(settings.companyTypeCode || settings.companyType || '').toUpperCase();
    if (!scope && country === 'NO' && ['ENK', 'ENKELTPERSONFORETAK'].includes(companyTypeCode)) {
      return { accountCode: '2060', requiresReview: false };
    }
    if (!scope && country === 'NO' && ['AS', 'ASA', 'AKSJESELSKAP'].includes(companyTypeCode)) {
      return { accountCode: '2915', requiresReview: false };
    }
    return { accountCode: undefined, requiresReview: true };
  };

  const paymentSourceForAccountCode = (accountCode) => {
    const code = String(accountCode || '').trim();
    if (/^206\d/.test(code) || /^2915(?::|$)/.test(code)) return 'private';
    if (code === '1900') return 'cash';
    return code ? 'company' : undefined;
  };

  // Slik brukeren selv har ført de samme kjøpene før. Kravet er strengt: minst
  // tre tidligere kjøp fra leverandøren, alle ført fra samme konto, ingen
  // motsatte. Ett tilfeldig kjøp sier ingenting om vanen, og et forslag som
  // spriker er verre enn ingen.
  const SUPPLIER_HISTORY_MINIMUM = 3;

  const supplierHistorySource = (state, analysis) => {
    const wanted = normalizeText(analysis && analysis.supplierName);
    if (!wanted) return undefined;
    const codes = list(state && state.expenses)
      .filter((expense) => expense && expense.status !== 'annulled' && !expense.annulledAt)
      .filter((expense) => normalizeText(expense.supplierName) === wanted)
      .map((expense) => String(
        expense.settlementAccountCode
        || (list(expense.paymentSplits).find((split) => split.settlementAccountCode && split.settlementAccountCode !== '2400') || {}).settlementAccountCode
        || '',
      ).trim())
      .filter(Boolean);
    if (codes.length < SUPPLIER_HISTORY_MINIMUM) return undefined;
    const unique = [...new Set(codes)];
    if (unique.length !== 1) return undefined;
    const paymentSource = paymentSourceForAccountCode(unique[0]);
    if (!paymentSource) return undefined;
    return {
      paymentSource,
      accountCode: unique[0],
      confidence: 'medium',
      origin: 'supplier-history',
      historyCount: codes.length,
      reason: `Foreslått fra historikken: de ${codes.length} siste kjøpene fra ${analysis.supplierName} er ført fra samme konto.`,
    };
  };

  // Foretakets eget standardvalg. Dette er ingen slutning fra bilaget — det er
  // brukerens erklæring om hva som gjelder når ingenting annet peker et sted.
  // Derfor er det siste utvei, og det merkes alltid i grensesnittet.
  const defaultAccountSource = (state) => {
    const code = String((state && state.settings && state.settings.defaultReceiptPaymentAccountCode) || '').trim();
    const paymentSource = paymentSourceForAccountCode(code);
    if (!paymentSource) return undefined;
    return {
      paymentSource,
      accountCode: code,
      confidence: 'low',
      origin: 'default',
      reason: 'Betalingskilden er ikke lest av bilaget. Foretakets standardkilde er forhåndsvalgt.',
    };
  };

  // Kortsifrene ligger på bilaget etter analysen. Registrerer brukeren kortet
  // *etterpå*, skal ikke nitti bilag måtte analyseres på nytt for å finne det ut:
  // oppslaget gjøres her, hver gang innboksen bygges. Samme grunn som at
  // banktreffet er avledet og ikke lagret.
  //
  // To kort med samme fire sifre kan ikke skilles på en kvittering. Da gis ingen
  // kilde — et gjett her ville bokført kjøpet på feil konto.
  const registeredCardMatches = (state, last4) => {
    const digits = String(last4 || '').replace(/\D/g, '').slice(-4);
    if (digits.length !== 4) return [];
    return list(state && state.settings && state.settings.bankAccounts)
      .filter((account) => account && account.active !== false)
      .flatMap((account) => list(account.cards)
        .filter((card) => String(card?.last4 || '').replace(/\D/g, '').slice(-4) === digits)
        .map(() => account));
  };

  const paymentCardSource = (state, analysis) => {
    const matches = registeredCardMatches(state, analysis && analysis.cardLast4);
    if (matches.length !== 1) return undefined;
    const account = matches[0];
    const isPrivate = String(account.kind || '').toLowerCase() === 'private';
    const privateSettlement = isPrivate ? privateSettlementForState(state) : null;
    return {
      paymentSource: isPrivate ? 'private' : 'company',
      accountCode: isPrivate
        ? privateSettlement.accountCode
        : String(account.ledgerAccountCode || account.ledger || account.accountCode || ''),
      confidence: 'high',
      origin: 'payment-card',
      requiresReview: isPrivate ? privateSettlement.requiresReview : !String(account.ledgerAccountCode || account.ledger || account.accountCode || ''),
      cardLast4: String(analysis.cardLast4).replace(/\D/g, '').slice(-4),
      reason: `Betalt med kortet **** ${String(analysis.cardLast4).replace(/\D/g, '').slice(-4)}, registrert på ${account.name || 'bankkontoen'}.`,
    };
  };

  // Kortsituasjonen er ferdig erklært når hver eneste betalingskonto enten har
  // kortene sine registrert, eller er krysset av for «har ikke kort». Først da
  // betyr «kortet på bilaget er ikke et av dem» noe: da *vet* vi hvilke kort
  // foretaket har. Mangler én konto begge deler, vet vi ingenting — og en tom
  // liste lest som «ingen kort finnes» ville vært gjetningen vi unngår.
  const companyCardsFullyDeclared = (state) => {
    const accounts = companyPaymentAccounts(state);
    return accounts.length > 0
      && accounts.every((account) => account.noCards === true || list(account.cards).length > 0);
  };

  const companyCardCount = (state) => companyPaymentAccounts(state)
    .reduce((total, account) => total + list(account.cards).length, 0);

  // Kortet på bilaget er ikke noen av foretakets kort. Da ble kjøpet ikke gjort
  // med foretakets kort, og et utlegg er den eneste forklaringen som står igjen.
  const unmatchedCardSource = (state, analysis) => {
    const digits = String((analysis && analysis.cardLast4) || '').replace(/\D/g, '').slice(-4);
    if (digits.length !== 4) return undefined;
    if (registeredCardMatches(state, digits).length > 0) return undefined;
    if (!companyCardsFullyDeclared(state)) return undefined;
    const settlement = privateSettlementForState(state);
    return {
      paymentSource: 'private',
      accountCode: settlement.accountCode,
      confidence: 'medium',
      origin: 'no-company-card',
      requiresReview: true,
      cardLast4: digits,
      reason: companyCardCount(state) > 0
        ? `Betalt med kortet **** ${digits}, som ikke er noen av foretakets registrerte kort. Ført som privat utlegg.`
        : `Betalt med kortet **** ${digits}. Ingen av foretakets kontoer har kort, så dette er ført som privat utlegg.`,
    };
  };

  // If the business has not configured any business payment account, a paid
  // purchase cannot truthfully be presented as paid from the company account.
  // Private outlay is therefore the useful preselection, but remains an
  // explicit review choice: missing bank setup is not proof of who paid.
  const noBusinessBankAccountSource = (state, analysis) => {
    if (hasConfiguredBusinessBankAccount(state)) return undefined;
    if (analysis?.documentKind === 'payment_confirmation') return undefined;
    if (analysis?.documentType === 'supplier_invoice' && analysis?.isPaid === false) return undefined;
    if (analysis?.suggestedPaymentSource === 'cash') return undefined;
    const settlement = privateSettlementForState(state);
    if (!settlement.accountCode) return undefined;
    return {
      paymentSource: 'private',
      accountCode: settlement.accountCode,
      confidence: 'low',
      origin: 'no-business-bank-account',
      requiresReview: true,
      reason: 'Foretaket har ingen registrert bedriftskonto. Privat utlegg er forhåndsvalgt og må bekreftes.',
    };
  };

  const suggest = (state, analysis) => {
    if (!analysis || analysis.status !== 'completed') return undefined;
    if (['manual', 'manual-at-upload'].includes(analysis.paymentSourceOrigin)) return undefined;

    const cardSource = paymentCardSource(state, analysis);
    if (cardSource) return cardSource;

    if (analysis.paymentEvidence?.origin === 'bank-match') return analysis.paymentEvidence;

    // Erklæringen om hvilke kort foretaket har er sterkere enn slutningen fra
    // bankdekning: den trenger ingen importert utskrift for å holde.
    const noCardSource = unmatchedCardSource(state, analysis);
    if (noCardSource) return noCardSource;

    const noBusinessBankAccount = noBusinessBankAccountSource(state, analysis);
    if (noBusinessBankAccount) return noBusinessBankAccount;

    // Modellens company/private er et forslag uten kontobevis. Det skal aldri
    // hindre bank, kort eller historikk i aa finne den faktiske kontoen.
    if (analysis.suggestedPaymentSource && analysis.suggestedPaymentSource !== 'unknown') return undefined;

    const amountNok = global.PlummeReceiptCurrency.grossNok(analysis);
    const coverage = amountNok != null && isIsoDate(analysis.receiptDate)
      ? coverageForDate(state, analysis.receiptDate)
      : { covered: false };
    // Ingen bankdekning: ingen slutning fra banken. Dette er skillet hele
    // regelen hviler på — og da faller vi videre til historikk og standardvalg,
    // som begge er merkede forslag og ikke slutninger fra bilaget.
    if (coverage.covered) {
      const settlement = privateSettlementForState(state);
      return {
        paymentSource: 'private',
        accountCode: settlement.accountCode,
        confidence: 'medium',
        origin: 'bank-absence',
        requiresReview: true,
        reason: 'Betalingen finnes ikke i bankutskriften som dekker denne datoen. Da er privat utlegg mest sannsynlig.',
      };
    }

    return supplierHistorySource(state, analysis) || defaultAccountSource(state);
  };

  global.PlummeReceiptPaymentSource = {
    companyCardsFullyDeclared,
    hasConfiguredBusinessBankAccount,
    noBusinessBankAccountSource,
    unmatchedCardSource,
    registeredCardMatches,
    paymentCardSource,
    paymentSourceForAccountCode,
    privateSettlementForState,
    supplierHistorySource,
    coverageForDate,
    suggest,
  };
})(typeof window !== 'undefined' ? window : globalThis);
